/**
 * ObstacleFunctions.js
 *
 * This module provides utility functions for managing obstacles (like chimneys, vents, skylights)
 * in the Eagle Eye Designer application. It handles operations such as:
 *
 * 1. Deleting obstacles from segments (now undo + redo)
 * 2. Selecting and highlighting obstacles
 * 3. Deselecting obstacles and restoring their original appearance
 * 4. Copy/paste of obstacles (now undo + redo)
 *
 * These functions complement the ObstacleManager class by providing operations
 * that occur after obstacle creation, particularly for user interaction.
 */

import * as THREE from "three";
import { POLYGONS, VARS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";
import { COLORS } from "../Global/materials";
import { addExtrude } from "./Modifications";
import { addOffset } from "./Shapes";
import {
    enableObstacleVertexDrag,
    disableObstacleVertexDrag,
} from "./ObstacleDragControls";
import { createSegmentPlane } from "../Global/functions";
import { updateLinkedSegmentsAfterMove } from "./ObstacleManager";
import { pushUndoAction } from "../Global/UndoManager";

/**
 * Deletes an obstacle from a segment (now undo + redo via UndoManager)
 *
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @param {number} obstacleIndex - Index of the obstacle to delete
 */
export function deleteObstacle(segmentIndex, obstacleIndex) {
    const selectedSegment = POLYGONS[segmentIndex];
    if (!selectedSegment) return;

    const selectedObs = selectedSegment.obstructions[obstacleIndex];
    if (!selectedObs) return;

    // Turn off vertex-drag mode so no more drag events fire for this obstacle.
    disableObstacleVertexDrag();

    // Snapshot obstacle data for undo/redo
    const parentSegmentIndex =
        typeof selectedObs.parentSegment === "number"
            ? selectedObs.parentSegment
            : segmentIndex;

    const cordsSnapshot = selectedObs.cords.map((p) => p.clone());
    const typeSnapshot = selectedObs.type;
    const heightSnapshot = selectedObs.height;
    const offsetSnapshot = selectedObs.offset;
    const colorSnapshot = selectedObs.ogColor || COLORS.maroon;

    const obstacleRef = selectedObs;

    // Delete immediately (this is the actual user action)
    internalDeleteObstacle(obstacleRef);

    console.log(
        "🗑️ Deleted obstacle from segments (undoable):",
        obstacleRef.linkedSegments,
        POLYGONS
    );

    // Register undo/redo
    pushUndoAction({
        label: "Delete obstacle",
        undo: () => {
            internalRecreateObstacle(
                obstacleRef,
                cordsSnapshot,
                typeSnapshot,
                heightSnapshot,
                offsetSnapshot,
                colorSnapshot,
                parentSegmentIndex
            );
        },
        redo: () => {
            internalDeleteObstacle(obstacleRef);
        },
    });
}

/**
 * Selects or deselects an obstacle
 *
 * @param {number} segmentIndexClicked - Segment index whose obstacle list you clicked in
 * @param {Object} selectedIndices - Current selection state { segmentIndex, obstacleIndex, panelIndex }
 * @param {number} index - Index of the obstacle in POLYGONS[segmentIndexClicked].obstructions
 * @param {Function} setSelectedIndices - Recoil setter for selection state
 */
export function selectObstacle(
    segmentIndexClicked,
    selectedIndices,
    index,
    setSelectedIndices
) {
    const { segmentIndex: prevSegmentIndex, obstacleIndex: prevObstacleIndex } =
        selectedIndices;

    if (
        segmentIndexClicked === null ||
        segmentIndexClicked === undefined
    )
        return;

    const seg = POLYGONS[segmentIndexClicked];
    if (!seg || !seg.obstructions || seg.obstructions.length === 0) return;

    if (index < 0 || index >= seg.obstructions.length) return;

    const selectedObs = seg.obstructions[index];

    // Already selected → deselect
    if (
        prevSegmentIndex === segmentIndexClicked &&
        prevObstacleIndex === index
    ) {
        setSelectedIndices({
            segmentIndex: segmentIndexClicked,
            obstacleIndex: null,
            panelIndex: null,
        });

        deselectObstacle(selectedObs);
        disableObstacleVertexDrag();
        return;
    }

    // Deselect previously selected obstacle on ANY segment
    if (
        prevSegmentIndex !== null &&
        prevSegmentIndex !== undefined &&
        prevObstacleIndex !== null &&
        prevObstacleIndex !== undefined
    ) {
        const prevSeg = POLYGONS[prevSegmentIndex];
        if (
            prevSeg &&
            prevSeg.obstructions &&
            prevSeg.obstructions[prevObstacleIndex]
        ) {
            const prevObs = prevSeg.obstructions[prevObstacleIndex];
            deselectObstacle(prevObs);
        }
    }

    if (selectedObs?.mesh?.material?.color) {
        VARS.selectedObs = {
            originalColor: selectedObs.mesh.material.color.clone(),
        };
        selectedObs.mesh.material.color.set(COLORS.yellow);
    }

    setSelectedIndices({
        segmentIndex: segmentIndexClicked,
        obstacleIndex: index,
        panelIndex: null,
    });

    enableObstacleVertexDrag(segmentIndexClicked, index);
}

/**
 * Deselects an obstacle and restores its original appearance
 */
export function deselectObstacle(selectedObs) {
    if (!selectedObs || !selectedObs.mesh || !selectedObs.mesh.material) return;
    selectedObs.mesh.material.color.set(COLORS.maroon);
}

// Copy currently selected obstacle into a simple data clipboard
export function copySelectedObstacle(selectedIndices) {
    const { segmentIndex, obstacleIndex } = selectedIndices;
    if (segmentIndex === null || obstacleIndex === null) return;

    const obs = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
    if (!obs) return;

    VARS.copiedObstacle = {
        segmentIndex,
        type: obs.type,
        height: obs.height,
        offset: obs.offset,
        ogColor: obs.ogColor || COLORS.maroon,
        cords: obs.cords.map((p) => p.clone()),
    };

    console.log("📋 Copied obstacle:", VARS.copiedObstacle);
}

/**
 * Build a 2D XY polygon for a given segment from its edges.
 */
function getSegmentPolygon2D(segmentIndex) {
    const segment = POLYGONS[segmentIndex];
    if (!segment || !segment.edges || !segment.edges.length) return null;

    const segPoints2D = [];
    segment.edges.forEach((edge) => {
        if (edge.point1)
            segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
        if (edge.point2)
            segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });

    const uniquePoints = [];
    for (let p of segPoints2D) {
        if (!uniquePoints.some((u) => u.distanceTo(p) < 1e-6))
            uniquePoints.push(p);
    }
    if (!uniquePoints.length) return null;

    const center = uniquePoints
        .reduce((acc, p) => acc.add(p), new THREE.Vector2())
        .divideScalar(uniquePoints.length);

    uniquePoints.sort(
        (a, b) =>
            Math.atan2(a.y - center.y, a.x - center.x) -
            Math.atan2(b.y - center.y, b.x - center.x)
    );

    return uniquePoints;
}

/**
 * Point-in-polygon test (2D XY) using ray casting.
 */
function pointInPolygon2D(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x,
            yi = polygon[i].y;
        const xj = polygon[j].x,
            yj = polygon[j].y;

        const intersect =
            yi > point.y !== yj > point.y &&
            point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
    }
    return inside;
}

/**
 * Find which segment is under a given ray by intersecting its plane + XY polygon.
 *
 * Returns { segmentIndex, hitPoint } or null if none.
 */
function findSegmentUnderRay(ray, camera, fallbackPlaneIndex = null) {
    const origin = ray.origin.clone();
    let best = null;

    POLYGONS.forEach((seg, idx) => {
        if (!seg) return;

        const plane = createSegmentPlane(idx);
        if (!plane) return;

        const hit = new THREE.Vector3();
        if (!ray.intersectPlane(plane, hit)) return;

        const poly = getSegmentPolygon2D(idx);
        if (!poly) return;

        const p2d = new THREE.Vector2(hit.x, hit.y);
        if (!pointInPolygon2D(p2d, poly)) return;

        const dist = origin.distanceTo(hit);
        if (!best || dist < best.distance) {
            best = { segmentIndex: idx, hitPoint: hit.clone(), distance: dist };
        }
    });

    return best ? { segmentIndex: best.segmentIndex, hitPoint: best.hitPoint } : null;
}

// Paste a copied obstacle at VARS.mouseNDC (now undo + redo)
export function pasteCopiedObstacle() {
    const data = VARS.copiedObstacle;
    if (!data) return;

    const ndc = VARS.mouseNDC || { x: 0, y: 0 };
    const mouse = new THREE.Vector2(ndc.x, ndc.y);

    // 1️⃣ Raycast from camera through mouse
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, sceneManager.camera);

    // 2️⃣ Find which segment is under the mouse
    const segHit = findSegmentUnderRay(raycaster.ray, sceneManager.camera);
    if (!segHit) {
        console.warn(
            "Paste: no segment under mouse – cannot determine parent segment"
        );
        return;
    }

    const parentSegmentIndex = segHit.segmentIndex;
    const pasteCenter = segHit.hitPoint;
    const segment = POLYGONS[parentSegmentIndex];
    if (!segment) return;

    // 3️⃣ Translate original cords so their center moves to pasteCenter
    const plane = createSegmentPlane(parentSegmentIndex);
    const oldCenter = getPointsCenter(data.cords);

    const translatedPoints = data.cords.map((p) => {
        const np = p.clone().sub(oldCenter).add(pasteCenter);
        if (plane) plane.projectPoint(np, np);
        return np;
    });

    // 4️⃣ Build shape + mesh + offset like obstacle creation
    const shapePoints = translatedPoints.map(
        (p) => new THREE.Vector2(p.x, p.y)
    );
    const shape = new THREE.Shape(shapePoints);

    const color = data.ogColor || COLORS.maroon;
    const extrudeMesh = addExtrude(
        translatedPoints,
        shape,
        data.height,
        color,
        sceneManager.scene
    );

    const { mesh, shape: finalShape, points } = addOffset(
        extrudeMesh,
        translatedPoints,
        data.type,
        shape,
        data.offset,
        data.height,
        parentSegmentIndex
    );

    // 5️⃣ Create a brand-new obstacle object (parented to the segment we pasted on)
    const obs = {
        type: data.type,
        polyShape: finalShape,
        cords: points,
        mesh,
        height: data.height,
        ogColor: color,
        offset: data.offset,
        parentSegment: parentSegmentIndex,
        linkedSegments: [parentSegmentIndex],
    };

    segment.obstructions.push(obs);
    const obstacleIndex = segment.obstructions.length - 1;

    // 6️⃣ Update overlaps with other segments
    // 6️⃣ Do NOT auto-attach to intersecting segments until user flushes
obs.isFlushed = false;
obs.linkedSegments = [parentSegmentIndex];


    console.log(
        "📎 Pasted obstacle at segment",
        parentSegmentIndex,
        "index",
        obstacleIndex,
        "linkedSegments:",
        obs.linkedSegments
    );

    // 🔴 Make paste undoable + redoable
    const obstacleRef = obs;
    const cordsSnapshot = obs.cords.map((p) => p.clone());
    const typeSnapshot = obs.type;
    const heightSnapshot = obs.height;
    const offsetSnapshot = obs.offset;
    const colorSnapshot = obs.ogColor || COLORS.maroon;

    pushUndoAction({
        label: "Paste obstacle",
        undo: () => {
            internalDeleteObstacle(obstacleRef);
        },
        redo: () => {
            internalRecreateObstacle(
                obstacleRef,
                cordsSnapshot,
                typeSnapshot,
                heightSnapshot,
                offsetSnapshot,
                colorSnapshot,
                parentSegmentIndex
            );
        },
    });

    // Returning so caller can update SegmentNavigator / selection state
    return { segmentIndex: parentSegmentIndex, obstacleIndex };
}

// ---------------------------------------------------------------------------
// INTERNAL HELPERS FOR DELETE / RECREATE (USED BY DELETE + PASTE UNDO/REDO)
// ---------------------------------------------------------------------------

function internalDeleteObstacle(obstruction) {
    if (!obstruction) return;

    // Remove mesh from scene
    if (obstruction.mesh) {
        const mesh = obstruction.mesh;
        if (mesh.parent) {
            mesh.parent.remove(mesh);
        } else {
            sceneManager.scene.remove(mesh);
        }
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) mesh.material.dispose();
        obstruction.mesh = null;
    }

    // Remove from all segments that reference it
    POLYGONS.forEach((seg) => {
        if (!seg || !Array.isArray(seg.obstructions)) return;
        seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
    });

    // Clear linked segments (they'll be recomputed on recreate)
    obstruction.linkedSegments = [];
}

/**
 * Rebuild obstacle mesh + offset and re-link it to segments.
 * Used for undo (delete) and redo (paste).
 */
function internalRecreateObstacle(
    obstruction,
    cordsSnapshot,
    typeSnapshot,
    heightSnapshot,
    offsetSnapshot,
    colorSnapshot,
    parentSegmentIndex
) {
    if (!obstruction) return;

    const basePoints = cordsSnapshot.map((p) => p.clone());

    const shapePoints = basePoints.map(
        (p) => new THREE.Vector2(p.x, p.y)
    );
    const shape = new THREE.Shape(shapePoints);

    const baseMesh = addExtrude(
        basePoints,
        shape,
        heightSnapshot,
        colorSnapshot,
        sceneManager.scene
    );

    const { mesh, shape: finalShape, points } = addOffset(
        baseMesh,
        basePoints,
        typeSnapshot,
        shape,
        offsetSnapshot,
        heightSnapshot,
        parentSegmentIndex
    );

    obstruction.type = typeSnapshot;
    obstruction.height = heightSnapshot;
    obstruction.offset = offsetSnapshot;
    obstruction.ogColor = colorSnapshot;
    obstruction.cords = points;
    obstruction.mesh = mesh;
    obstruction.polyShape = finalShape;
    obstruction.parentSegment = parentSegmentIndex;

    if (typeof obstruction.isFlushed !== "boolean") obstruction.isFlushed = false;


    // Recompute linked segments using the shared helper
    updateLinkedSegmentsAfterMove(obstruction);
}

function getPointsCenter(points) {
    const center = new THREE.Vector3();
    points.forEach((p) => center.add(p));
    center.divideScalar(points.length || 1);
    return center;
}
